import React, {Fragment, useContext, useEffect} from 'react';
import {ApiUtility, InvoiceListApiObject} from "../common/ApiUtility";
import {OverlayLoadingIndicator} from "../common/OverlayLoadingIndicator";
import {ListPaginationOptions} from "../common/ListPaginationOptions";
import {GlobalContext, GlobalContextType} from "../admin-frame/AdminFrame";
import {CommonListPageHeader} from "../common/CommonListPageHeader";
import {ConditionalRenderer} from "../common/ConditionalRenderer";
import {StringUtility} from "../common/StringUtility";
import {useRenderableData} from "../common/hooks/useRenderableData";
import {UrlUtility} from "../common/UrlUtility";
import {MessageDisplayerUtility} from "../common/MessageDisplayerUtility";
import {SingleInvoice} from "./SingleInvoice";
import {InvoiceDataUtility} from "../common/InvoiceDataUtility";
import {Constants} from "../common/Constants";
import {CreateSubscription} from "../subscriptions/CreateSubscription";
import {DateUtility} from "../common/DateUtility";
import {RouteComponentProps} from 'react-router-dom';
import {FpInvoice, Invoice} from "./InvoiceTypes";
import {Subscription} from "../subscriptions/SubscriptionTypes";
import {DelayedSearchBar} from "../common/DelayedSearchBar";
import {DateRangeFilter} from "../common/DateRangeFilter";
import {SelectUtility} from "../common/SelectUtility";
import {SelectOption} from "../common/CommonUIElementTypes";
import Select from 'react-select';

interface InvoicesListPageURLQueryParams {
    page?: number;
    searchText?: string;
    startDate?: string;
    endDate?: string;
    fpClientId?: number;
}

interface InvoicesListPageRouteParams {
    invoiceId: string | undefined;
    fpInvoiceId: string | undefined;
    fpInvoiceIdForSubscription: string | undefined;
}

interface InvoicesListPageState {
    selectedPage: number;
    searchText: string;
    filterStartDateString: string;
    filterEndDateString: string;
    filterFpClientId?: number;
    showSingleInvoiceModal: boolean;
    showMakeSubscriptionModal: boolean;
    singleInvoiceId?: number;
    singleFpInvoiceId?: number;
    invoiceList: InvoiceListApiObject[];
    fpClientOptions: object[];
    pageCount: number;
    loading: boolean;
    prevUrlQueryString?: string;
    prevParams?: InvoicesListPageRouteParams;
}

export const InvoicesListPage = function (
    props: RouteComponentProps<InvoicesListPageRouteParams>
) {
    const context: GlobalContextType = useContext(GlobalContext);
    const urlBase = '/invoices';
    const initialState: InvoicesListPageState = {
        selectedPage: 1,
        searchText: '',
        filterStartDateString: '',
        filterEndDateString: '',
        filterFpClientId: undefined,
        showSingleInvoiceModal: false,
        showMakeSubscriptionModal: false,
        singleInvoiceId: undefined,
        singleFpInvoiceId: undefined,
        invoiceList: [],
        fpClientOptions: [],
        pageCount: 0,
        loading: false,

        prevUrlQueryString: undefined,
        prevParams: undefined,
    }
    const renderableData = useRenderableData(initialState)

    // Update state if URL changes.
    if (
        (props.location && props.location.search) !== renderableData.prevUrlQueryString ||
        (props.match && props.match.params) !== renderableData.prevParams
    ) {
        let selectedPage = 1;
        let searchText = '';
        let filterStartDateString = '';
        let filterEndDateString = '';
        let filterFpClientId = undefined;
        let singleInvoiceId = null;
        let singleFpInvoiceId = null;
        let showSingleInvoiceModal = false;
        let showMakeSubscriptionModal = false;

        let routeParams = props.match && props.match.params;
        if (
            routeParams &&
            (routeParams['invoiceId'] || routeParams['fpInvoiceId'])
        ) {
            showSingleInvoiceModal = true;
            singleInvoiceId = routeParams['invoiceId'];
            singleFpInvoiceId = routeParams['fpInvoiceId'];
        }

        if (
            routeParams &&
            routeParams['fpInvoiceIdForSubscription']
        ) {
            singleFpInvoiceId = routeParams['fpInvoiceIdForSubscription'];
            showMakeSubscriptionModal = true;
        }

        let queryParams: InvoicesListPageURLQueryParams = UrlUtility.getQueryParamsFromProps(props);
        if (queryParams.page) {
            selectedPage = queryParams.page;
        }
        if (queryParams.searchText) {
            searchText = queryParams.searchText;
        }
        if (queryParams.startDate) {
            filterStartDateString = queryParams.startDate;
        }
        if (queryParams.endDate) {
            filterEndDateString = queryParams.endDate;
        }
        if (queryParams.fpClientId) {
            filterFpClientId = queryParams.fpClientId;
        }

        renderableData.updateAndRender({
            selectedPage: selectedPage,
            searchText: searchText,
            filterStartDateString: filterStartDateString,
            filterEndDateString: filterEndDateString,
            filterFpClientId: filterFpClientId,
            showSingleInvoiceModal: showSingleInvoiceModal || false,
            showMakeSubscriptionModal: showMakeSubscriptionModal || false,
            singleInvoiceId: singleInvoiceId,
            singleFpInvoiceId: singleFpInvoiceId,

            prevUrlQueryString: props.location && props.location.search,
            prevParams: props.match && props.match.params,
        })
    }

    // Trigger reload of list data on mount and if certain values change in state.
    useEffect(
        function () {
            reloadList();
        },
        [
            '' + renderableData.selectedPage,
            '' + renderableData.searchText,
            '' + renderableData.filterStartDateString,
            '' + renderableData.filterEndDateString,
            '' + renderableData.filterFpClientId
        ]
    );

    function reloadList(callback?: Function) {
        let renderableDataUpdate: Partial<InvoicesListPageState> = {};
        renderableData.updateAndRender({loading: true})
        ApiUtility.getInvoicesList(
            renderableData.selectedPage,
            renderableData.searchText,
            renderableData.filterStartDateString,
            renderableData.filterEndDateString,
            renderableData.filterFpClientId
        )
            .then(function (listData) {
                renderableDataUpdate = {
                    ...listData,
                    loading: false
                };

                return new Promise<void>(function (resolve, reject) {
                    if (renderableData.fpClientOptions.length === 0) {
                        return ApiUtility.getFpCompaniesList()
                            .then(function (fpCompaniesList) {
                                renderableDataUpdate.fpClientOptions = SelectUtility.modelArrayToSelectOptions(
                                    fpCompaniesList, 'id', 'name'
                                )
                                resolve()
                            })
                    } else {
                        resolve()
                    }
                });
            })
            .then(function () {
                renderableData.updateAndRender(renderableDataUpdate);
                if (callback) {
                    callback();
                }
            })
            .catch(function (error) {
                renderableData.updateAndRender({
                    loading: false
                });
                console && console.error(error);
                MessageDisplayerUtility.error(`An error occurred while loading the invoice list.`);
            });
    }

    function goToSingleInvoiceModal(invoiceId: number | undefined, fpInvoiceId: number | undefined) {
        if (invoiceId) {
            context.navigator.navigateTo(`${urlBase}/${invoiceId}${props.location.search}`);
        } else {
            context.navigator.navigateTo(`${urlBase}/fp-invoice/${fpInvoiceId}${props.location.search}`);
        }
    }

    function goToMakeSubscriptionModal(fpInvoiceId: number) {
        context.navigator.navigateTo(`${urlBase}/make-subscription-from-fp-invoice/${fpInvoiceId}${props.location.search}`);
    }

    function goToSubscription(subscriptionId: number) {
        context.navigator.navigateTo(`/subscriptions/${subscriptionId}`);
    }

    function closeModals() {
        context.navigator.navigateTo(`${urlBase}${props.location.search}`);
    }

    function performSearch(searchText: string) {
        if (renderableData.searchText !== searchText) {
            let queryParams: InvoicesListPageURLQueryParams = UrlUtility.getQueryParamsFromProps(props);
            queryParams.searchText = searchText;
            let queryString = UrlUtility.getUrlQueryStringFromQueryParamsObject(queryParams);
            context.navigator.navigateTo(`${urlBase}${queryString}`);
        }
    }

    function filterByDateRange(filterStartDateString: string, filterEndDateString: string) {
        if (
            renderableData.filterStartDateString !== filterStartDateString ||
            renderableData.filterEndDateString !== filterEndDateString
        ) {
            let queryParams: InvoicesListPageURLQueryParams = UrlUtility.getQueryParamsFromProps(props);
            queryParams.startDate = filterStartDateString;
            queryParams.endDate = filterEndDateString;
            queryParams.page = 1;
            let queryString = UrlUtility.getUrlQueryStringFromQueryParamsObject(queryParams);
            context.navigator.navigateTo(`${urlBase}${queryString}`);
        }
    }

    function filterByFpClientId(option: SelectOption) {
        if (renderableData.filterFpClientId !== option?.value) {
            let queryParams: InvoicesListPageURLQueryParams = UrlUtility.getQueryParamsFromProps(props);
            queryParams.fpClientId = option?.value as number || undefined;
            queryParams.page = 1;
            let queryString = UrlUtility.getUrlQueryStringFromQueryParamsObject(queryParams);
            context.navigator.navigateTo(`${urlBase}${queryString}`);
        }
    }

    function afterSuccessfulSave(invoiceId: number) {
        closeModals()
        reloadList(
            () => goToSingleInvoiceModal(invoiceId, undefined)
        );
    }

    function afterSuccessfulCancel() {
        closeModals()
        reloadList()
    }

    function renderInvoicesListDisplay(invoicesList: InvoiceListApiObject[]) {
        let invoicesListDisplay = [];

        for (let i = 0; i < invoicesList.length; i++) {
            const invoiceObject = invoicesList[i];
            let fpInvoice = invoiceObject.fpInvoice;
            let invoice = invoiceObject.invoice;
            let description = fpInvoice.description || '';
            if (description.length > 200) {
                description = description.substring(0, 200) + '...';
            }
            let invoiceStatusClass = InvoiceDataUtility.invoiceStatusToClass(invoice.invoice_status)
            let invoiceStatusDisplay = (
                <div className="invoice-status-content">
                    <div className="invoice-status-content-top">
                        <div className={`invoice-status-badge ${invoiceStatusClass}`}>
                            {invoice.invoice_status?.name}
                        </div>
                        <ConditionalRenderer
                            if={invoice.subsequent_auto_sync_failures >= InvoiceDataUtility.getMaxAllowedAutoSyncFailures()}
                            renderer={() => (
                                <img
                                    className="warning-triangle"
                                    title={Constants.invoiceAutoSyncDisabledMessage}
                                    src={'/static/images/warning-error-red.svg'}
                                />
                            )}
                        />
                    </div>
                    <div className="invoice-status-additional-info">
                        {invoice.invoice_status?.additional_info}
                    </div>
                </div>
            )
            let invoiceType = Constants.invoiceTypes.fpOnly;
            let onClickInvoiceFunction =
                () => goToSingleInvoiceModal(invoiceObject.invoice.id, invoiceObject.fpInvoice.id)
            if (invoice.id) {
                invoiceType = Constants.invoiceTypes.oneOff
            } else if (invoiceObject.subscription_from_fp_invoice) {
                let subscriptionId = invoiceObject.subscription_from_fp_invoice.id
                invoiceType = Constants.invoiceTypes.subscription
                onClickInvoiceFunction = () => goToSubscription(subscriptionId)
                invoiceStatusDisplay = (
                    <div className="invoice-status-content">
                        <div
                            className="invoice-status-badge subscription"
                            title="Used to create a subscription"
                        >
                            SUBSCRIPTION
                        </div>
                    </div>
                )
            }

            let invoiceNumber = invoice.custom_invoice_number || fpInvoice.invoicenumber
            let fpInvoiceNumber = null;
            if (
                invoice.custom_invoice_number &&
                invoice.custom_invoice_number !== fpInvoice.invoicenumber
            ) {
                fpInvoiceNumber = (
                    <div className="fp-invoice-number">
                        {`(FP: ${fpInvoice.invoicenumber})`}
                    </div>
                )
            }

            let fpInvoiceDate = DateUtility.fpInvoiceDateToDisplayDate(fpInvoice.invoicedate)

            invoicesListDisplay.push(
                <div
                    key={i + 1}
                    id={`invoice-row-${fpInvoice.id}`}
                    className="common-list-row common-list-values-row invoices-list-row"
                    onClick={onClickInvoiceFunction}
                >
                    <div className="common-list-row-content">
                        <div
                            className="common-list-row-cell common-list-row-value-cell invoices-list-row-cell invoice-type"
                        >
                            <img
                                src={invoiceType.iconSrc}
                                title={invoiceType.name}
                                className={`invoice-number-icon ${invoiceType.className}`}
                            />
                        </div>
                        <div
                            className="common-list-row-cell common-list-row-value-cell invoices-list-row-cell invoice-number"
                        >
                            <div className="invoice-number-cell-content">
                                <div className="invoice-number-cell-content-left">
                                    <div className="invoice-number-details">
                                        <div className="billing-system-invoice-number">{invoiceNumber}</div>
                                        {fpInvoiceNumber}
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div
                            className="common-list-row-cell common-list-row-value-cell invoices-list-row-cell invoice-date"
                        >{fpInvoiceDate}</div>
                        <div
                            className="common-list-row-cell common-list-row-value-cell invoices-list-row-cell company-name"
                        >{fpInvoice.hCompanyname}</div>
                        <div
                            className="common-list-row-cell common-list-row-value-cell invoices-list-row-cell invoice-description"
                        >{description}</div>
                        <div
                            className="common-list-row-cell common-list-row-value-cell invoices-list-row-cell invoice-total"
                        >{StringUtility.stringNumberToDisplayCurrency(invoice.billed_total || fpInvoice.grandusertotal)}</div>
                        <div
                            className="common-list-row-cell common-list-row-value-cell invoices-list-row-cell invoice-status"
                        >
                            {invoiceStatusDisplay}
                        </div>
                    </div>
                </div>
            );
        }

        return invoicesListDisplay;
    }

    if (renderableData.invoiceList) {
        return (
            <div className="common-list-page invoices-list-page">
                <CommonListPageHeader
                    headerText="Invoices"
                    rightHeaderRenderer={() => {
                        return (
                            <Fragment>
                                <Select
                                    placeholder="Filter by client"
                                    className="common-form-select header-filter-select fp-company-filter-select"
                                    classNamePrefix="common-form-select"
                                    value={SelectUtility.selectOptionForId(
                                        renderableData.filterFpClientId,
                                        renderableData.fpClientOptions
                                    )}
                                    onChange={(option) => filterByFpClientId(option)}
                                    options={renderableData.fpClientOptions}
                                    isClearable={true}
                                />
                                <DateRangeFilter
                                    filterByDateRange={filterByDateRange}
                                    filterStartDateString={renderableData.filterStartDateString}
                                    filterEndDateString={renderableData.filterEndDateString}
                                />
                                <DelayedSearchBar
                                    performSearch={performSearch}
                                    value={renderableData.searchText}
                                    placeholder="Search Invoice Numbers"
                                />
                            </Fragment>
                        )
                    }}
                />
                <div className="common-list-page-content">
                    <ConditionalRenderer
                        if={renderableData.loading}
                        renderer={() => <OverlayLoadingIndicator/>}
                    />
                    {(() => {
                        if (renderableData.invoiceList.length > 0) {
                            return (
                                <Fragment>
                                    <div className="common-list-header">
                                        <div className="common-list-row common-list-labels-row">
                                            <div className="common-list-row-content">
                                                <div
                                                    className="common-list-row-cell common-list-row-label-cell invoices-list-row-cell invoice-type"
                                                >Type
                                                </div>
                                                <div
                                                    className="common-list-row-cell common-list-row-label-cell invoices-list-row-cell invoice-number"
                                                >Number
                                                </div>
                                                <div
                                                    className="common-list-row-cell common-list-row-label-cell invoices-list-row-cell invoice-date"
                                                >Date (FP)
                                                </div>
                                                <div
                                                    className="common-list-row-cell common-list-row-label-cell invoices-list-row-cell company-name"
                                                >Client
                                                </div>
                                                <div
                                                    className="common-list-row-cell common-list-row-label-cell invoices-list-row-cell invoice-description"
                                                >Description
                                                </div>
                                                <div
                                                    className="common-list-row-cell common-list-row-label-cell invoices-list-row-cell invoice-total"
                                                >Total
                                                </div>
                                                <div
                                                    className="common-list-row-cell common-list-row-label-cell invoices-list-row-cell invoice-status"
                                                >Status
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="common-list-content">
                                        {renderInvoicesListDisplay(renderableData.invoiceList)}
                                    </div>
                                    <ListPaginationOptions
                                        pageCount={renderableData.pageCount}
                                        selectedPage={renderableData.selectedPage}
                                        urlBase="/invoices"
                                        location={props.location}
                                    />
                                </Fragment>
                            );
                        } else {
                            return (
                                <div className="common-empty-list-message-container">
                                    <div className="common-empty-list-message">No invoices found.</div>
                                </div>
                            );
                        }
                    })()}
                </div>
                {(() => {
                    if (renderableData.showSingleInvoiceModal) {
                        return (
                            <SingleInvoice
                                cancel={closeModals}
                                invoiceId={renderableData.singleInvoiceId}
                                fpInvoiceId={renderableData.singleFpInvoiceId}
                                afterSuccessfulSave={afterSuccessfulSave}
                                afterSuccessfulCancel={afterSuccessfulCancel}
                                onClickMakeSubscription={goToMakeSubscriptionModal}
                            />
                        );
                    } else if (renderableData.showMakeSubscriptionModal) {
                        return (
                            <CreateSubscription
                                cancel={closeModals}
                                afterSuccessfulSave={goToSubscription}
                                fromFpInvoiceId={renderableData.singleFpInvoiceId}
                            />
                        )
                    }
                })()}
            </div>
        );
    } else {
        return (
            <OverlayLoadingIndicator/>
        );
    }
};
